import { DeleteIcon } from "@chakra-ui/icons";
import { Button, Text, useDisclosure } from "@chakra-ui/react";
import { FC, useState } from "react";

import { AddIcon, ColumnDefinition, RanaModal, RanaTable } from "@nens/ranalib";
import { Schematisation, SchematisationRevision } from "@nens/threedi/openapi-client";

import { APP_BASE_HREF } from "../../App";
import { useNeedsCreatorRoleTitle } from "../../queries/general";
import {
  DeleteSchematisationRevisionItem,
  useDeleteMultipleSchematisationRevisions,
  useRevisions,
} from "../../queries/schematisations";
import { useDeleteMultipleModelsBySchematisationRevisions } from "../../queries/threedimodels";
import RemoveMultipleModal from "../RemoveMultipleModal";

interface ChooseRevisionButtonProps {
  schematisation: Schematisation;
}

const ChooseRevisionButton: FC<ChooseRevisionButtonProps> = ({ schematisation }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const needsCreatorTitle = useNeedsCreatorRoleTitle();
  const {
    isOpen: isOpenDeleteModelModal,
    onOpen: onOpenDeleteModelModal,
    onClose: onCloseDeleteModelModal,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteRevisionModal,
    onOpen: onOpenDeleteRevisionModal,
    onClose: onCloseDeleteRevisionModal,
  } = useDisclosure();
  const revisionsResponse = useRevisions(schematisation.id ?? 0);
  const [deleteIds, setDeleteIds] = useState<DeleteSchematisationRevisionItem[]>([]);

  // Call these hooks before any early return
  const deleteModelsMutation = useDeleteMultipleModelsBySchematisationRevisions(deleteIds);
  const deleteRevisionsMutation = useDeleteMultipleSchematisationRevisions(deleteIds);

  if (schematisation.id === undefined) {
    return null;
  }

  const data =
    revisionsResponse.data?.results.map((result: SchematisationRevision) => ({
      uuid: "" + result.id,
      ...result,
    })) || [];

  const columnDefinitions: ColumnDefinition[] = [
    {
      title: "#",
      width: "table_cell_xxs",
    },
    {
      title: "Author",
      width: "table_cell_xs",
    },
    {
      title: "Commit message",
      width: "table_cell_xs",
    },
    {
      title: "Created",
      width: "table_cell_xs",
    },
    {
      title: "Valid",
      width: "table_cell_xxs",
    },
    {
      title: "HCC Model",
      width: "table_cell_xxs",
    },
  ];

  return (
    <>
      <Button variant="secondary" leftIcon={<AddIcon boxSize={5} />} ml={6} mb={1} onClick={onOpen}>
        Choose other revision
      </Button>
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        topBar="Choose a revision"
        buttons={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <RanaTable
          columnDefinitions={columnDefinitions}
          tableDataIds={data.map((row) => row.uuid)}
          isLoading={!revisionsResponse.isSuccess}
          emptyElement={<RanaTable.Empty />}
          tableActions={[
            {
              label: "Delete selected HCC models",
              getDisabledText: () => needsCreatorTitle,
              icon: <DeleteIcon boxSize={4} mr={3} />,
              action: (rowIds) => {
                const rows = data.filter((row) => rowIds.includes(row.uuid));
                setDeleteIds(
                  rows
                    .filter((row) => row.schematisationId !== undefined && row.id !== undefined)
                    .map((row) => ({
                      schematisationId: row.schematisationId!,
                      revisionId: row.id!,
                    }))
                );
                onOpenDeleteModelModal();
              },
            },
            {
              label: "Delete selected revisions",
              getDisabledText: () =>
                schematisation.ranaProjectId !== null
                  ? "This schematisation is part of a Rana project and cannot be modified here."
                  : needsCreatorTitle,
              icon: <DeleteIcon boxSize={4} mr={3} />,
              action: (rowIds) => {
                const rows = data.filter((row) => rowIds.includes(row.uuid));
                setDeleteIds(
                  rows
                    .filter(
                      (row) =>
                        row.schematisationId !== undefined &&
                        row.id !== undefined &&
                        row.number !== undefined
                    )
                    .map((row) => ({
                      schematisationId: row.schematisationId!,
                      revisionId: row.id!,
                      revisionNumber: row.number!,
                    }))
                );
                onOpenDeleteRevisionModal();
              },
            },
          ]}
          sx={{
            // vertical alignment for the icons and checkboxes
            "& p": {
              marginBottom: 0,
            },
            "& label": {
              marginBottom: 0,
            },
          }}
        >
          <RanaTable.Container>
            <RanaTable.Header />
            <RanaTable.Body>
              {data.map((row, index) => (
                <RanaTable.Row
                  to={`${APP_BASE_HREF}/schematisations/${schematisation.id}/${row.id}`}
                  onClick={onClose}
                  key={`${row.uuid}${index}`}
                  rowId={row.uuid}
                >
                  <RanaTable.Cell>{row.number}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <Text>{row.commitUser}</Text>
                  </RanaTable.Cell>
                  <RanaTable.Cell title={row.commitMessage || undefined}>
                    {row.commitMessage}
                  </RanaTable.Cell>
                  <RanaTable.Cell>
                    <Text>{row.commitDate?.toLocaleDateString()}</Text>
                  </RanaTable.Cell>
                  <RanaTable.Cell>
                    <Text>{row.isValid ? "Y" : "N"}</Text>
                  </RanaTable.Cell>
                  <RanaTable.Cell>
                    <Text>{row.hasThreedimodel ? "Y" : "N"}</Text>
                  </RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
        </RanaTable>
        <RemoveMultipleModal
          isOpen={isOpenDeleteModelModal}
          onClose={onCloseDeleteModelModal}
          deleteMultipleFunction={deleteModelsMutation}
        >
          This deletes the HCC models if they exist. This action cannot be undone.
        </RemoveMultipleModal>
        <RemoveMultipleModal
          isOpen={isOpenDeleteRevisionModal}
          onClose={onCloseDeleteRevisionModal}
          deleteMultipleFunction={deleteRevisionsMutation}
        >
          This deletes all the revision. If they also have a HCC model, they will also be deleted.
          This action cannot be undone.
        </RemoveMultipleModal>
      </RanaModal>
    </>
  );
};

export default ChooseRevisionButton;
